// Helpers for re-running a report query cell whose qid is already taken.
//
// A `query_answers` document's id is immutable and equals the cell's qid, so
// re-running an edited cell must happen under a *fresh* qid. The backend rejects
// a run whose qid already has a stored answer; it signals that specific
// rejection with HTTP 409 + `error_code: "QID_EXISTS"` so the frontend can
// transparently re-run under a new qid instead of silently dropping the run.
// See issue #10539.

export const QID_CONFLICT_CODE = "QID_EXISTS";

/** True when a run response is the backend's "this qid already has an answer"
 * rejection -- the precise, non-sensitive signal to re-run under a fresh qid. */
export function isQidConflictResponse(
  status: number,
  body: { error_code?: string } | null | undefined
): boolean {
  return status === 409 && body?.error_code === QID_CONFLICT_CODE;
}

/** True when redux already holds an answer for this qid, so a submit under it
 * would collide with an immutable id and must instead run under a fresh qid. */
export function hasStoredAnswer(
  qid: string,
  queryAnswers: ReadonlyArray<{ qid?: string }> | undefined | null
): boolean {
  return Boolean(queryAnswers?.some((qa) => qa.qid === qid));
}

export interface RerunRunResult {
  ok: boolean;
  status: number;
  /** Lazily read the (cloned) response body; only called when `ok` is false. */
  readBody: () => Promise<{ error_code?: string } | null>;
}

/**
 * Run a query and, if the backend rejects the qid as already-answered
 * (409/QID_EXISTS), re-run exactly once after `rewire` re-points the cell at a
 * fresh qid. Any other non-ok result (or a second failure) is surfaced via
 * `onFail`. Extracted from `handleSubmit` so the retry decision is unit-tested.
 */
export async function submitWithRerunOnConflict(deps: {
  run: () => Promise<RerunRunResult>;
  rewire: () => Promise<void>;
  onFail: (status: number | undefined) => void;
}): Promise<void> {
  let res = await deps.run();
  if (!res.ok) {
    const body = await res.readBody();
    if (isQidConflictResponse(res.status, body)) {
      await deps.rewire();
      res = await deps.run();
    }
  }
  if (!res.ok) {
    deps.onFail(res.status);
  }
}
